import { extractLinkRegistryFromChatbotResponse } from '@/lib/chatbotDeepLinks';

const CHAT_SESSION_STORAGE_PREFIX = 'neo-chatbot-session';

export { extractLinkRegistryFromChatbotResponse };

export function chatSessionStorageKey(tenantId, userId) {
  return `${CHAT_SESSION_STORAGE_PREFIX}::${tenantId || 'default'}::${userId || 'anonymous'}`;
}

export function loadStoredChatSessionId(tenantId, userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(chatSessionStorageKey(tenantId, userId));
    return raw && String(raw).trim() ? String(raw).trim() : null;
  } catch {
    return null;
  }
}

export function saveStoredChatSessionId(tenantId, userId, sessionId) {
  if (!userId || !sessionId) return;
  try {
    localStorage.setItem(chatSessionStorageKey(tenantId, userId), String(sessionId));
  } catch {
    // ignore quota errors
  }
}

export function clearStoredChatSessionId(tenantId, userId) {
  if (!userId) return;
  try {
    localStorage.removeItem(chatSessionStorageKey(tenantId, userId));
  } catch {
    // ignore
  }
}

const ACCOUNT_ROW_UUID_RE =
  /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

/** `accounts.id` (row UUID) vs the chatbot's `accounts.account_id` business key. */
export function isAccountRowUuid(value) {
  return typeof value === 'string' && ACCOUNT_ROW_UUID_RE.test(value.trim());
}

/** Business key from a loaded account record — never `.id`. */
export function chatbotBusinessAccountId(record) {
  if (!record || typeof record !== 'object') return undefined;
  const key = record.account_id ?? record.accountId;
  if (key == null) return undefined;
  const s = String(key).trim();
  return s || undefined;
}

function matchAccountForChatbotAnchor(accounts, lookupId) {
  if (!lookupId || !Array.isArray(accounts)) return null;
  const key = String(lookupId);
  return (
    accounts.find(
      (a) => String(a?.id) === key || String(a?.account_id) === key,
    ) || null
  );
}

/**
 * Account id to post on `/api/chatbot/ask`.
 * Prefer `account.account_id` (business key). Never the AccountFinal `?id=` row UUID.
 */
export function resolveChatbotAccountId(context, options = {}) {
  const { routeRecordId, accounts, pageAccount } = options;

  const fromLoaded = chatbotBusinessAccountId(pageAccount)
    || chatbotBusinessAccountId(context?.pageAccount)
    || chatbotBusinessAccountId(context?.preselectedAccount)
    || chatbotBusinessAccountId(context?.account);
  if (fromLoaded) return fromLoaded;

  const ctxId = context?.account_id ?? context?.accountId;
  const ctxStr = ctxId != null ? String(ctxId).trim() : '';
  if (ctxStr && !isAccountRowUuid(ctxStr)) return ctxStr;

  const lookupId = routeRecordId || (isAccountRowUuid(ctxStr) ? ctxStr : undefined);
  const fromList = chatbotBusinessAccountId(matchAccountForChatbotAnchor(accounts, lookupId));
  if (fromList) return fromList;

  return undefined;
}

/** Prefer the business key; fall back to a UUID only if nothing else is in hand. */
export function chatbotAskAccountIdFromSources({
  override,
  selectedAccountId,
  resolved,
} = {}) {
  const candidates = [override, selectedAccountId, resolved];
  const business = candidates.find((id) => {
    const s = id != null ? String(id).trim() : '';
    return s && !isAccountRowUuid(s);
  });
  if (business) return String(business).trim();
  const any = candidates.find((id) => id != null && String(id).trim());
  return any != null ? String(any).trim() : undefined;
}

/** Nested `resolved_account` from POST /api/chatbot/ask — not flat resolved_account_name. */
export function accountFromResolvedAccount(resolved) {
  if (!resolved || typeof resolved !== 'object') return null;
  const account_id = String(resolved.account_id ?? resolved.accountId ?? '').trim();
  const account_name = String(resolved.account_name ?? resolved.accountName ?? '').trim();
  if (!account_id && !account_name) return null;
  return {
    account_id: account_id || null,
    account_name: account_name || null,
  };
}

export function buildChatbotAskBody({
  question,
  tenantId,
  userId,
  sessionId,
  accountId,
  language,
  debug,
  stream,
  context,
}) {
  const body = {
    question: String(question || '').trim(),
    tenant_id: tenantId,
    user_id: userId,
  };
  if (sessionId) body.session_id = sessionId;
  if (accountId) body.account_id = accountId;
  if (language) body.language = language;
  if (debug) body.debug = true;
  if (stream) body.stream = true;
  if (context && typeof context === 'object') body.context = context;
  return body;
}

function parseSseEventBlock(block) {
  if (!block || !String(block).trim()) return null;
  let eventName = 'message';
  const dataLines = [];
  for (const line of String(block).split('\n')) {
    if (line.startsWith('event:')) eventName = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^\s/, ''));
  }
  const dataStr = dataLines.join('\n');
  if (!dataStr) return null;
  try {
    return { event: eventName, data: JSON.parse(dataStr) };
  } catch {
    return null;
  }
}

function normalizeChipTargetIds(item) {
  const targetPersonId = item?.target_person_id ?? item?.targetPersonId ?? null;
  const targetAccountId = item?.target_account_id ?? item?.targetAccountId ?? null;
  return {
    targetPersonId:
      targetPersonId != null && String(targetPersonId).trim()
        ? String(targetPersonId).trim()
        : null,
    targetAccountId:
      targetAccountId != null && String(targetAccountId).trim()
        ? String(targetAccountId).trim()
        : null,
  };
}

function normalizeSuggestedNext(raw) {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const label = String(item.label ?? item.text ?? item.prompt ?? '').trim();
      const prompt = String(item.prompt ?? item.label ?? item.text ?? '').trim();
      const actionType = String(
        item.action_type ?? item.actionType ?? 'prefill_question',
      ).trim();
      const { targetPersonId, targetAccountId } = normalizeChipTargetIds(item);

      if (actionType === 'compose_via_persona') {
        if (!targetPersonId) return null;
        return {
          id: item.id != null ? String(item.id) : null,
          label: label || String(item.target_person_name ?? item.targetPersonName ?? 'Draft message').trim(),
          prompt: '',
          actionType,
          targetPersonId,
          targetPersonName: item.target_person_name ?? item.targetPersonName ?? null,
          targetAccountId,
          composeBrief: String(item.compose_brief ?? item.composeBrief ?? '').trim(),
        };
      }

      if (actionType === 'navigate') {
        if (!targetPersonId && !targetAccountId) return null;
        return {
          id: item.id != null ? String(item.id) : null,
          label: label || prompt || String(item.target_person_name ?? item.targetPersonName ?? 'Open profile').trim(),
          prompt: '',
          actionType,
          targetPersonId,
          targetAccountId,
          targetPersonName: item.target_person_name ?? item.targetPersonName ?? null,
        };
      }

      if (actionType === 'add_account_to_workspace' || actionType === 'request_account_from_admin') {
        if (!targetAccountId) return null;
        const targetAccountName = item.target_account_name ?? item.targetAccountName ?? null;
        return {
          id: item.id != null ? String(item.id) : null,
          label: label || prompt,
          prompt: '',
          actionType,
          targetPersonId,
          targetAccountId,
          targetAccountName: targetAccountName != null ? String(targetAccountName).trim() || null : null,
        };
      }

      if (!label && !prompt) return null;

      return {
        id: item.id != null ? String(item.id) : null,
        label: label || prompt,
        prompt: prompt || label,
        actionType,
        targetPersonId,
        targetAccountId,
      };
    })
    .filter(Boolean);
}

export function normalizeAccountNotProvisioned(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const kind = String(raw.kind ?? '').trim();
  if (!kind) return null;
  const poolAccountId = String(raw.pool_account_id ?? raw.poolAccountId ?? '').trim();
  const accountName = String(raw.account_name ?? raw.accountName ?? '').trim();
  const country = String(raw.country ?? '').trim();
  return {
    kind,
    poolAccountId: poolAccountId || null,
    accountName: accountName || null,
    country: country || null,
  };
}

export function isWorkspaceAccountChipAction(actionType) {
  const t = String(actionType || '').trim();
  return t === 'add_account_to_workspace' || t === 'request_account_from_admin';
}

export function requestChipFromNotProvisioned(info, accountId, accountName) {
  const name = String(accountName || info?.accountName || 'this account').trim() || 'this account';
  return {
    id: null,
    label: `Request ${name} from NEO`,
    prompt: '',
    actionType: 'request_account_from_admin',
    targetPersonId: null,
    targetAccountId: String(accountId || info?.poolAccountId || '').trim() || null,
    targetAccountName: name,
  };
}

export function normalizeChatbotMessageId(raw) {
  if (raw == null) return null;
  const id = String(raw).trim();
  return id || null;
}

const TABLE_COLUMN_KINDS = new Set(['text', 'number', 'date', 'badge', 'link']);
const CHART_KINDS = new Set(['pie', 'bar']);

function optionalNote(value) {
  return value != null && String(value).trim() ? String(value).trim() : undefined;
}

function normalizeTableBlock(block) {
  if (!block?.id || !Array.isArray(block.columns) || !Array.isArray(block.rows)) return null;
  const columns = block.columns
    .filter((col) => col && col.key)
    .map((col) => ({
      key: String(col.key),
      label: String(col.label ?? col.key),
      kind: TABLE_COLUMN_KINDS.has(col.kind) ? col.kind : 'text',
    }));
  if (!columns.length) return null;
  if (!block.rows.length) return null;
  return {
    type: 'table',
    id: String(block.id),
    title: String(block.title ?? ''),
    columns,
    rows: block.rows,
    note: optionalNote(block.note),
  };
}

function normalizeChartBlock(block) {
  if (!block?.id || !CHART_KINDS.has(block.chart) || !Array.isArray(block.slices)) return null;
  const slices = block.slices
    .filter((slice) => slice && slice.label != null && Number.isFinite(Number(slice.value)))
    .map((slice) => ({
      label: String(slice.label),
      value: Number(slice.value),
      ...(slice.other === true ? { other: true } : {}),
    }));
  if (!slices.length) return null;
  const denominator = Number(block.denominator);
  return {
    type: 'chart',
    chart: block.chart,
    id: String(block.id),
    title: String(block.title ?? ''),
    unit: String(block.unit ?? '').trim() || 'institutions',
    ...(Number.isFinite(denominator) ? { denominator } : {}),
    slices,
    note: optionalNote(block.note),
  };
}

export function normalizeChatBlocks(raw) {
  if (raw == null) return undefined;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;

  const blocks = raw
    .map((block) => {
      if (!block) return null;
      if (block.type === 'table') return normalizeTableBlock(block);
      if (block.type === 'chart') return normalizeChartBlock(block);
      return null;
    })
    .filter(Boolean);

  return blocks.length ? blocks : undefined;
}

export function buildBotMessageFromChatbotResponse(data, { isStreaming = false } = {}) {
  const blocks = normalizeChatBlocks(data?.blocks);
  return {
    sender: 'bot',
    text: data?.answer || "I couldn't find an answer.",
    id: Date.now(),
    messageId: normalizeChatbotMessageId(data?.message_id ?? data?.messageId),
    intent: data?.intent,
    resolvedAccount: data?.resolved_account ?? null,
    citations: Array.isArray(data?.citations) ? data.citations : [],
    candidates:
      data?.intent === 'disambiguation' && Array.isArray(data?.candidates)
        ? data.candidates
        : undefined,
    suggestedNext: normalizeSuggestedNext(
      data?.suggested_next ?? data?.suggestedNext ?? data?.chips,
    ),
    accountNotProvisioned: normalizeAccountNotProvisioned(
      data?.account_not_provisioned ?? data?.accountNotProvisioned,
    ),
    linkRegistry: extractLinkRegistryFromChatbotResponse(data),
    language: data?.language,
    latencyMs: data?.latency_ms,
    isStreaming,
    ...(blocks ? { blocks } : {}),
  };
}

/**
 * POST /api/chatbot/feedback — thumbs rating for an assistant answer.
 * Returns { ok: true } | { ok: false, status, error, unavailable }.
 */
export async function postChatbotFeedback({
  apiBaseUrl,
  messageId,
  rating,
  reason,
  tenantId,
  userId,
  sessionId,
  headers = {},
  credentials = 'include',
  signal,
}) {
  const mid = normalizeChatbotMessageId(messageId);
  if (!mid) {
    return { ok: false, status: 0, error: 'missing message_id', unavailable: false };
  }
  if (rating !== 'up' && rating !== 'down') {
    return { ok: false, status: 400, error: 'bad rating', unavailable: false };
  }

  const body = {
    message_id: mid,
    rating,
    tenant_id: tenantId,
    user_id: userId,
  };
  const sid = sessionId != null ? String(sessionId).trim() : '';
  if (sid) body.session_id = sid;
  const trimmedReason = reason != null ? String(reason).trim() : '';
  if (trimmedReason) body.reason = trimmedReason;

  const url = `${String(apiBaseUrl || '').replace(/\/$/, '')}/api/chatbot/feedback`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    credentials,
    body: JSON.stringify(body),
    signal,
  });

  if (response.ok) {
    return { ok: true, status: response.status };
  }

  let error = `HTTP ${response.status}`;
  try {
    const json = await response.json();
    error = json?.error || json?.message || error;
  } catch {
    // keep default
  }

  return {
    ok: false,
    status: response.status,
    error,
    unavailable: response.status === 503,
  };
}

async function parseChatbotJson(response) {
  const text = await response.text().catch(() => '');
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

/** POST /api/chatbot/account-request */
export async function postChatbotAccountRequest({
  apiBaseUrl,
  payload,
  headers = {},
  credentials = 'include',
  signal,
}) {
  const url = `${String(apiBaseUrl || '').replace(/\/$/, '')}/api/chatbot/account-request`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    credentials,
    body: JSON.stringify(payload || {}),
    signal,
  });
  const data = await parseChatbotJson(response);
  if (!response.ok) {
    const err = new Error(data?.error || data?.message || `HTTP ${response.status}`);
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return data;
}

/** GET /api/chatbot/account-requests */
export async function fetchChatbotAccountRequests({
  apiBaseUrl,
  status = 'open',
  tenantId,
  headers = {},
  credentials = 'include',
  signal,
}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (tenantId) params.set('tenant_id', tenantId);
  const qs = params.toString();
  const url = `${String(apiBaseUrl || '').replace(/\/$/, '')}/api/chatbot/account-requests${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { ...headers },
    credentials,
    signal,
  });
  const data = await parseChatbotJson(response);
  if (!response.ok) {
    const err = new Error(data?.error || data?.message || `HTTP ${response.status}`);
    err.status = response.status;
    err.body = data;
    throw err;
  }
  if (Array.isArray(data)) return data;
  return data?.requests || data?.rows || data?.items || [];
}

/** POST /api/chatbot/account-request/:id/resolve */
export async function resolveChatbotAccountRequest({
  apiBaseUrl,
  requestId,
  payload,
  headers = {},
  credentials = 'include',
  signal,
}) {
  const id = encodeURIComponent(String(requestId || '').trim());
  const url = `${String(apiBaseUrl || '').replace(/\/$/, '')}/api/chatbot/account-request/${id}/resolve`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    credentials,
    body: JSON.stringify(payload || {}),
    signal,
  });
  const data = await parseChatbotJson(response);
  if (!response.ok) {
    const err = new Error(data?.error || data?.message || `HTTP ${response.status}`);
    err.status = response.status;
    err.body = data;
    throw err;
  }
  return data;
}

/**
 * POST /api/chatbot/ask?stream=true — consumes SSE token/done/error events.
 * Falls back to JSON body when the server returns a non-SSE response.
 */
export async function consumeChatbotAskStream({
  apiBaseUrl,
  body,
  headers = {},
  credentials = 'include',
  onToken,
  onBlocks,
  onDone,
  onError,
  signal,
}) {
  const url = `${apiBaseUrl.replace(/\/$/, '')}/api/chatbot/ask?stream=true`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = errorText;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorJson.message || errorText;
    } catch {
      // use raw text
    }
    throw new Error(errorMessage || `HTTP ${response.status}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/event-stream') || !response.body) {
    const data = await response.json();
    onDone?.(data);
    return data;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let donePayload = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';

    for (const block of events) {
      const parsed = parseSseEventBlock(block);
      if (!parsed) continue;

      if (parsed.event === 'token') {
        const delta = parsed.data?.delta;
        if (typeof delta === 'string' && delta.length > 0) onToken?.(delta);
      } else if (parsed.event === 'blocks') {
        onBlocks?.(parsed.data);
      } else if (parsed.event === 'done') {
        donePayload = parsed.data;
        onDone?.(parsed.data);
      } else if (parsed.event === 'error') {
        const errMsg = parsed.data?.error || parsed.data?.message || 'Stream failed';
        onError?.(errMsg);
        throw new Error(errMsg);
      }
    }
  }

  if (buffer.trim()) {
    const parsed = parseSseEventBlock(buffer);
    if (parsed?.event === 'done') {
      donePayload = parsed.data;
      onDone?.(parsed.data);
    } else if (parsed?.event === 'error') {
      const errMsg = parsed.data?.error || parsed.data?.message || 'Stream failed';
      onError?.(errMsg);
      throw new Error(errMsg);
    }
  }

  if (!donePayload) {
    throw new Error('Chatbot stream ended without a done event');
  }

  return donePayload;
}
